/**
 * Cloudinary URL helpers.
 *
 * Cloudinary serves the original asset unless the URL asks for something smaller,
 * so a 8043x5365 master gets downloaded to fill a 321x321 slot. Inserting a width
 * (plus `f_auto`/`q_auto`) into the delivery URL is what actually fixes that.
 *
 * WHY these live here: they are pure string transforms, so they stay testable and
 * usable from both the native <img> paths and, later, a next/image loader.
 */

/**
 * Cloudinary is also served from SCOTT-owned CNAMEs, not just res.cloudinary.com,
 * so hostname alone is not enough to detect it — see `isCloudinaryUrl`.
 * Extend this list as further Cloudinary-backed domains are confirmed.
 */
export const CLOUDINARY_HOSTNAMES = [
  "res.cloudinary.com",
  "static.scott-sports.com",
];

const UPLOAD_SEGMENT = "/image/upload/";

/**
 * `f_auto`, `w_640,c_limit`, `e_blur:300` — one transformation component.
 * Versions (`v1771320`) never match: they carry no underscore.
 */
const TRANSFORM_SEGMENT = /^[a-z]{1,3}_[^/]+$/;

/** Widths offered to the browser when the rendered size is not known up front. */
// biome-ignore-start lint/style/noMagicNumbers: breakpoint ladder offered to the browser
export const CLOUDINARY_WIDTH_LADDER = [
  48, 64, 96, 128, 256, 320, 480, 640, 768, 1024, 1280, 1536, 1920, 2560, 3072,
  3840,
];
// biome-ignore-end lint/style/noMagicNumbers: breakpoint ladder offered to the browser

export function isCloudinaryUrl(src: string): boolean {
  try {
    const url = new URL(src);
    return (
      CLOUDINARY_HOSTNAMES.includes(url.hostname) &&
      url.pathname.includes(UPLOAD_SEGMENT)
    );
  } catch {
    // Relative paths, data URIs and blob URLs are never Cloudinary.
    return false;
  }
}

/**
 * Vector sources are left alone entirely: `f_auto` rasterises them, which defeats
 * the point of shipping an SVG, and a width means nothing to a drawing.
 */
function isVectorAsset(src: string): boolean {
  return /\.svg($|[?#])/i.test(src);
}

function parameterKey(parameter: string): string {
  return parameter.split("_")[0];
}

/**
 * Adds `parameter` unless the URL already sets that key — an explicit `w_500`
 * from the caller or the CMS always wins over a derived one.
 */
function addParameter(parameters: string[], parameter: string): void {
  if (parameters.some((p) => parameterKey(p) === parameterKey(parameter))) {
    return;
  }
  parameters.push(parameter);
}

function findFourThreeCanvasIndex(segments: string[]): number {
  return segments.findIndex((segment) => {
    const parameters = segment.split(",");
    return (
      parameters.includes("ar_4:3") &&
      parameters.includes("c_auto_pad") &&
      parameters.includes("g_auto")
    );
  });
}

/** The transformation component derived parameters are merged into, or -1. */
function findTransformIndex(segments: string[]): number {
  const paddedCanvasIndex = findFourThreeCanvasIndex(segments);
  if (paddedCanvasIndex >= 0) {
    return paddedCanvasIndex;
  }
  return TRANSFORM_SEGMENT.test(segments[0]) ? 0 : -1;
}

function getDeliverySegments(src: string): string[] {
  return src
    .slice(src.indexOf(UPLOAD_SEGMENT) + UPLOAD_SEGMENT.length)
    .split("/");
}

/**
 * A width already in the URL wins over a derived one (see `addParameter`), so a
 * srcSet built from it would point every descriptor at the same file and the
 * browser could pick, then upscale, an undersized rendition.
 */
function hasExplicitWidth(src: string): boolean {
  const segments = getDeliverySegments(src);
  const transformIndex = findTransformIndex(segments);
  return (
    transformIndex >= 0 &&
    segments[transformIndex]
      .split(",")
      .some((parameter) => parameterKey(parameter) === "w")
  );
}

/**
 * Adds `f_auto`, `q_auto` and an optional width to a Cloudinary delivery URL,
 * returning anything else untouched.
 *
 * Parameters are merged *into* the existing transformation component rather than
 * chained as a new one, so Cloudinary resizes in a single pass:
 *   `/upload/f_auto,q_auto/v1/x.jpg` -> `/upload/f_auto,q_auto,w_640,c_limit/v1/x.jpg`
 */
export function withCloudinaryTransform(src: string, width?: number): string {
  if (!isCloudinaryUrl(src) || isVectorAsset(src)) {
    return src;
  }

  const insertAt = src.indexOf(UPLOAD_SEGMENT) + UPLOAD_SEGMENT.length;
  const segments = getDeliverySegments(src);
  const transformIndex = findTransformIndex(segments);
  const parameters =
    transformIndex >= 0 ? segments[transformIndex].split(",") : [];

  addParameter(parameters, "f_auto");
  addParameter(parameters, "q_auto");
  if (width && width > 0) {
    addParameter(parameters, `w_${Math.round(width)}`);
    if (findFourThreeCanvasIndex(segments) < 0) {
      addParameter(parameters, "c_limit");
    }
  }

  if (transformIndex >= 0) {
    segments[transformIndex] = parameters.join(",");
  } else {
    segments.unshift(parameters.join(","));
  }

  return `${src.slice(0, insertAt)}${segments.join("/")}`;
}

/** Fits product imagery in a 4:3 card canvas without trimming soft shadows. */
export function withCloudinaryFourThreePadding(src: string): string {
  if (!isCloudinaryUrl(src) || isVectorAsset(src)) {
    return src;
  }

  const insertAt = src.indexOf(UPLOAD_SEGMENT) + UPLOAD_SEGMENT.length;
  const rest = src.slice(insertAt);
  const segments = rest.split("/");
  const firstSegment = segments[0];
  if (findFourThreeCanvasIndex(segments) >= 0) {
    return src;
  }

  const hasTransform = TRANSFORM_SEGMENT.test(firstSegment);
  const parameters = hasTransform ? firstSegment.split(",") : [];
  const tail = hasTransform ? rest.slice(firstSegment.length + 1) : rest;

  const background =
    parameters.find((parameter) => parameterKey(parameter) === "b") ??
    "b_white";
  const cardParameters = parameters.filter(
    (parameter) => !["ar", "c", "b", "h", "w"].includes(parameterKey(parameter))
  );
  const width = parameters.find((parameter) => parameterKey(parameter) === "w");
  const canvasParameters = ["ar_4:3", "c_auto_pad", "g_auto", background];
  if (width) {
    canvasParameters.push(width);
  }

  const originalTransform = cardParameters.length
    ? `${cardParameters.join(",")}/`
    : "";
  return `${src.slice(0, insertAt)}${originalTransform}${canvasParameters.join(",")}/${tail}`;
}

/**
 * Width-based `srcSet` for images whose rendered size is only known at layout time
 * (fill mode). Returns undefined for non-Cloudinary sources and URLs that already
 * pin a width, so the caller can fall back to whatever it already had.
 */
export function buildCloudinarySrcSet(
  src: string,
  widths: number[] = CLOUDINARY_WIDTH_LADDER
): string | undefined {
  if (!isCloudinaryUrl(src) || isVectorAsset(src) || hasExplicitWidth(src)) {
    return;
  }

  return widths
    .map((width) => `${withCloudinaryTransform(src, width)} ${width}w`)
    .join(", ");
}

/**
 * Density-based `srcSet` for images rendered at a known CSS width, so high-DPR
 * screens stay sharp without every screen paying for the 2x asset.
 */
export function buildCloudinaryDprSrcSet(
  src: string,
  width: number
): string | undefined {
  if (
    !isCloudinaryUrl(src) ||
    isVectorAsset(src) ||
    hasExplicitWidth(src) ||
    !width ||
    width <= 0
  ) {
    return;
  }

  return [1, 2]
    .map((dpr) => `${withCloudinaryTransform(src, width * dpr)} ${dpr}x`)
    .join(", ");
}
